import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVerticalIcon } from "lucide-react";
import Link from "next/link";
import { AspirationResponse } from "../aspiration/interface";
import {
  formatDateDay,
  getStatusBgColor,
  getStatusText,
  getStatusTextColor,
} from "@/lib/utils";
import { useRegionNames } from "../../regions/api";
import Image from "next/image";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { usePatchAspirationStatus } from "../aspiration/api";

const baseColumn = (
  currentPage: number,
  perPage: number
): ColumnDef<AspirationResponse>[] => [
  {
    accessorKey: "id",
    header: () => <div className="whitespace-nowrap">No</div>,
    cell: ({ row }) => {
      const number = (currentPage - 1) * perPage + row.index + 1;
      return <div>{number}</div>;
    },
  },
  {
    accessorKey: "name",
    header: () => <div className="whitespace-nowrap">Nama Masyarakat</div>,
    cell: ({ row }) => row.original.name,
  },
  {
    accessorKey: "proposal",
    header: () => <div className="whitespace-nowrap">Judul Pengajuan</div>,
    cell: ({ row }) => row.original.proposal,
  },
  {
    accessorKey: "createdAt",
    header: () => <div className="whitespace-nowrap">Tanggal Pengajuan</div>,
    cell: ({ row }) => formatDateDay(row.original.createdAt),
  },
  {
    accessorKey: "status",
    header: () => <div className="whitespace-nowrap">Status</div>,
    cell: ({ row }) => {
      const status = row.original.status;

      return (
        <div
          className={`text-sm flex items-center gap-2 ${getStatusTextColor(status) || "text-gray-500"}`}
        >
          <div
            className={`w-2 h-2 flex-shrink-0 ${getStatusBgColor(status)} h-2 rounded-full `}
          ></div>
          {getStatusText(status) || status}
        </div>
      );
    },
  },
];

const baseProcectedColumn = (
  currentPage: number,
  perPage: number
): ColumnDef<AspirationResponse>[] => [
  ...baseColumn(currentPage, perPage).slice(0, 3),
  {
    accessorKey: "dapil.name",
    header: "Dapil",
    cell: ({ row }) => row.original.dapil?.name || "-",
  },
  {
    accessorKey: "subDistrict",
    header: "Kecamatan",
    cell: ({ row }) => <KecamatanName code={row.original?.subDistrict} />,
  },
  {
    accessorKey: "angota.name",
    header: "Wakil Rakyat",
    cell: ({ row }) => row.original.anggota?.name || "-",
  },
  {
    accessorKey: "categoryAspirationId",
    header: "Kategori Usulan",
    cell: ({ row }) => row.original.categoryAspiration?.name || "-",
  },
  ...baseColumn(currentPage, perPage).slice(4),
];

export const subsColumns = (
  currentPage: number,
  perPage: number
): ColumnDef<AspirationResponse>[] => [
  ...baseProcectedColumn(currentPage, perPage),
  {
    id: "actions",
    header: "Aksi",
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center justify-center w-[30px] h-[30px] hover:bg-gray-100 rounded">
          <MoreVerticalIcon size={16} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <Link href={`/submission-aspirations/detail/${row.original.id}`}>
            <DropdownMenuItem className="cursor-pointer">
              Detail
            </DropdownMenuItem>
          </Link>
          <Link href={`/submission-aspirations/edit/${row.original.id}`}>
            <DropdownMenuItem className="cursor-pointer">Edit</DropdownMenuItem>
          </Link>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

export const subsColumnsAcceptedHistory = (
  currentPage: number,
  perPage: number
): ColumnDef<AspirationResponse>[] => [
  ...baseProcectedColumn(currentPage, perPage),
  {
    accessorKey: "isActive",
    header: "Status",
    cell: ({ row }) => <ComponentUpdateStatus item={row.original} />,
  },
  {
    id: "actions",
    header: "Aksi",
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center justify-center w-[30px] h-[30px] hover:bg-gray-100 rounded">
          <MoreVerticalIcon size={16} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <Link href={`/submission-aspirations/detail/${row.original.id}`}>
            <DropdownMenuItem className="cursor-pointer">
              Detail
            </DropdownMenuItem>
          </Link>
          <Link href={`/submission-aspirations/edit/${row.original.id}`}>
            <DropdownMenuItem className="cursor-pointer">Edit</DropdownMenuItem>
          </Link>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

export const subsColumnsWithoutMember = (
  currentPage: number,
  perPage: number
): ColumnDef<AspirationResponse>[] => [
  ...baseColumn(currentPage, perPage),
  {
    id: "actions",
    header: "Aksi",
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center justify-center w-[30px] h-[30px] hover:bg-gray-100 rounded">
          <MoreVerticalIcon size={16} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <Link href={`/aspiration/detail/${row.original.id}`}>
            <DropdownMenuItem className="cursor-pointer">
              Detail
            </DropdownMenuItem>
          </Link>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

export const subsColumnGuessUser = (
  currentPage: number,
  perPage: number
): ColumnDef<AspirationResponse>[] => [
  ...baseColumn(currentPage, perPage).slice(0, 4),
  {
    accessorKey: "categoryAspirationId",
    header: () => <div className="whitespace-nowrap">Kategori Usulan</div>,
    cell: ({ row }) => row.original.categoryAspiration?.name || "-",
  },
  {
    accessorKey: "dapil.name",
    header: "Dapil",
    cell: ({ row }) => row.original.dapil?.name || "-",
  },
  {
    accessorKey: "fraksi.name",
    header: "Partai",
    cell: ({ row }) => row.original.anggota?.fraksi?.name || "-",
  },
  {
    accessorKey: "anggota.fraksi.logo",
    header: () => <div className="whitespace-nowrap">Logo Fraksi</div>,
    cell: ({ row }) => (
      <Image
        src={row.original.anggota?.fraksi.logo || "/assets/images/no-image.png"}
        alt={
          `${row.original.anggota?.fraksi.name}-${row.original.anggota?.fraksi.id}` ||
          ""
        }
        width={80}
        height={80}
        className="object-contain w-20 h-20"
      />
    ),
  },
  {
    accessorKey: "anggota.name",
    header: "Wakil Rakyat",
    cell: ({ row }) => row.original.anggota?.name || "-",
  },
  ...baseColumn(currentPage, perPage).slice(4),
  {
    id: "actions",
    header: "Aksi",
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center justify-center w-[30px] h-[30px] hover:bg-gray-100 rounded">
          <MoreVerticalIcon size={16} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <Link href={`/aspiration/detail/${row.original.id}`}>
            <DropdownMenuItem className="cursor-pointer">
              Detail
            </DropdownMenuItem>
          </Link>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

const KecamatanName = ({ code }: { code: string }) => {
  const { district, districtLoading } = useRegionNames({ district: code }, [
    "district",
  ]);

  if (districtLoading)
    return <div className="w-20 h-4 animate-pulse bg-gray-300 rounded-md" />;
  return <div>{district || "-"}</div>;
};

const ComponentUpdateStatus = ({ item }: { item: AspirationResponse }) => {
  const queryClient = useQueryClient();
  const updateStatusMutation = usePatchAspirationStatus(String(item.id));

  const handleUpdateStatus = (isActive: boolean) => {
    updateStatusMutation.mutate(
      {},
      {
        onSuccess: (data) => {
          toast.success("Berhasil Update Data Status");
          console.log(data, "data response");
        },
        onError: (error: any) => {
          toast.error("Gagal Update Data Status");
        },
        onSettled: () => {
          queryClient.invalidateQueries(["useAspiration"]);
        },
      }
    );
  };

  return (
    <Switch
      checked={item.isPublished}
      onCheckedChange={(checked) => handleUpdateStatus(checked)}
    />
  );
};
